// src/components/nanny/NannyFilters.jsx
import { useState, useEffect } from 'react';
import api from '../../lib/api';
import FormInput from '../forms/FormInput';
import FormSelect from '../forms/FormSelect';

const religionOptions = [
  { value: '', label: 'Any Religion' },
  { value: 'christian', label: 'Christian' },
  { value: 'muslim', label: 'Muslim' },
  { value: 'traditional', label: 'Traditional' },
  { value: 'other', label: 'Other / Prefer not to say' },
];

export default function NannyFilters({ onFilter }) {
  const [filters, setFilters] = useState({
    search: '',
    state_of_origin: '',
    religion: '',
    min_salary: '',
    max_salary: '',
    min_age: '',
    max_age: '',
    live_in: '',
    vetted_only: true,
  });

  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(true);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const res = await api.get('/states');
        const stateData = res.data.data || [];
        const formatted = [
          { value: '', label: 'Any State' },
          ...stateData.map(s => ({
            value: s.id.toString(),
            label: s.name
          }))
        ];
        setStates(formatted);
      } catch (err) {
        console.error('Failed to load states');
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, []);

  const handleChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    onFilter(newFilters);
  };

  const handleClear = () => {
    const cleared = {
      search: '',
      state_of_origin: '',
      religion: '',
      min_salary: '',
      max_salary: '',
      min_age: '',
      max_age: '',
      live_in: '',
      vetted_only: true,
    };
    setFilters(cleared);
    onFilter(cleared);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
      {/* Header */}
      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
        <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
        Find Your Ideal Nanny
      </h3>

      {/* Scrollable container - this is the key fix */}
      <div className="max-h-96 sm:max-h-none overflow-y-auto pb-48 -mx-6 px-6 sm:mx-0 sm:px-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-4">
          {/* Search */}
          <FormInput
            label="Search by name or phone"
            value={filters.search}
            onChange={(e) => handleChange('search', e.target.value)}
            placeholder="e.g Aisha, 08012345678"
          />

          {/* State */}
          <FormSelect
            label="State of Origin"
            options={states}
            value={states.find(opt => opt.value === filters.state_of_origin)}
            onChange={(opt) => handleChange('state_of_origin', opt?.value || '')}
            placeholder={loadingStates ? 'Loading...' : 'Any state'}
            disabled={loadingStates}
            isSearchable
          />

          {/* Religion */}
          <FormSelect
            label="Religion"
            options={religionOptions}
            value={religionOptions.find(opt => opt.value === filters.religion)}
            onChange={(opt) => handleChange('religion', opt?.value || '')}
            placeholder="Any religion"
          />

          {/* Min Salary */}
          <FormInput
            label="Min Salary (₦)"
            type="number"
            value={filters.min_salary}
            onChange={(e) => handleChange('min_salary', e.target.value)}
            placeholder="50,000"
          />

          {/* Max Salary */}
          <FormInput
            label="Max Salary (₦)"
            type="number"
            value={filters.max_salary}
            onChange={(e) => handleChange('max_salary', e.target.value)}
            placeholder="200,000"
          />

          {/* Min Age */}
          <FormInput
            label="Min Age"
            type="number"
            value={filters.min_age}
            onChange={(e) => handleChange('min_age', e.target.value)}
            placeholder="18"
          />

          {/* Max Age */}
          <FormInput
            label="Max Age"
            type="number"
            value={filters.max_age}
            onChange={(e) => handleChange('max_age', e.target.value)}
            placeholder="50"
          />

          {/* Live-in */}
          <FormSelect
            label="Live-in Required?"
            options={[
              { value: '', label: 'Any' },
              { value: '1', label: 'Yes (Live-in)' },
              { value: '0', label: 'No (Live-out)' },
            ]}
            value={
              filters.live_in === '1'
                ? { value: '1', label: 'Yes (Live-in)' }
                : filters.live_in === '0'
                ? { value: '0', label: 'No (Live-out)' }
                : { value: '', label: 'Any' }
            }
            onChange={(opt) => handleChange('live_in', opt?.value || '')}
          />

          {/* Vetted Only - Full width on mobile */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4">
            <label className="flex items-center gap-3 cursor-pointer text-lg">
              <input
                type="checkbox"
                checked={filters.vetted_only}
                onChange={(e) => handleChange('vetted_only', e.target.checked)}
                className="w-6 h-6 text-primary rounded focus:ring-primary"
              />
              <span className="font-semibold text-gray-800">Vetted Only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Clear Button - Sticky at bottom */}
      <div className="mt-6 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
        <button
          onClick={handleClear}
          className="w-full text-center text-primary hover:underline font-medium text-lg py-2"
        >
          Clear all filters
        </button>
      </div>
    </div>
  );
}